import React, { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Sidebar from '../components/Sidebar';

// Security function to sanitize user input and prevent XSS
const sanitizeText = (text) => {
  if (!text) return '';
  
  // Convert to string and remove any HTML tags and scripts
  const cleanText = String(text)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[BLOCKED SCRIPT]')
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
    
  return cleanText;
};

// Additional security for displaying user content safely
const SecureText = ({ children, className = '', title = '' }) => {
  const sanitizedText = sanitizeText(children);
  const sanitizedTitle = sanitizeText(title);
  
  return (
    <span 
      className={className} 
      title={sanitizedTitle}
      dangerouslySetInnerHTML={undefined} // Prevent any HTML injection
    >
      {sanitizedText}
    </span>
  );
};

const ChallengesList = () => {
  const { admin, isAuthenticated, isLoading } = useAdmin();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [challenges, setChallenges] = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Filter and pagination states
  const [statusFilter, setStatusFilter] = useState('all'); // all, à-venir, en-cours, terminé
  const [thematicFilter, setThematicFilter] = useState('all'); // all, nourriture, sport, musique, art, gaming
  const [displayLength, setDisplayLength] = useState(12); // 12, 24, 50
  const [currentPage, setCurrentPage] = useState(1);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Redirect admin users to their own dashboard
  // TEMPORARILY DISABLED - allowing all admins to access challenges list
  /*
  useEffect(() => {
    if (!isLoading && isAuthenticated && admin?.owner === 'admin') {
      console.log('🚫 Admin user trying to access streamer challenges list, redirecting to admin dashboard');
      navigate('/admin/dashboard');
    }
  }, [admin, isAuthenticated, isLoading, navigate]);
  */

  // Toast function
  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  }, []);

  const fetchChallenges = useCallback(async () => {
    setLoadingChallenges(true);
    try {
      // Fetch all challenges (not just active ones) for admin review
      const response = await fetch('/.netlify/functions/get-challenges');
      
      console.log('📡 Challenges API Response Status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('📦 Challenges API Response:', result);
      
      if (result.success) {
        // Handle both response formats: result.data (activeOnly) and result.challenges (normal)
        const challengesData = result.data || result.challenges || [];
        // Filter out the "normal" option which has id "normal" - we only want real challenges
        const realChallenges = challengesData.filter(challenge => challenge.id !== 'normal' && challenge.id !== 0);
        
        setChallenges(realChallenges);
        console.log('✅ Fetched challenges:', realChallenges);
        
        if (realChallenges.length === 0) {
          console.log('📝 No challenges found in database');
        }
      } else {
        console.error('❌ Failed to fetch challenges:', result.message);
        setChallenges([]);
        showToastMessage(result.message || t('errorLoadingChallenges'), 'error');
      }
    } catch (error) {
      console.error('❌ Error fetching challenges:', error);
      setChallenges([]);
      showToastMessage(t('networkErrorLoadingChallenges'), 'error');
    } finally {
      setLoadingChallenges(false);
    }
  }, [showToastMessage, t]);

  // Fetch challenges on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchChallenges();
    }
  }, [isAuthenticated, fetchChallenges]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get challenge status
  const getChallengeStatus = (challenge) => {
    const now = new Date();
    const startDate = challenge.start_date ? new Date(challenge.start_date) : null;
    const endDate = challenge.expiration ? new Date(challenge.expiration) : null;

    console.log(`🔍 Status check for "${challenge.title}":`, {
      now: now.toISOString(),
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      nowTime: now.getTime(),
      startTime: startDate?.getTime(),
      endTime: endDate?.getTime()
    });

    if (endDate && now > endDate) {
      console.log(`✅ Challenge "${challenge.title}" is FINISHED (now > endDate)`);
      return { status: t('finished'), color: 'bg-gray-500', key: 'terminé' };
    } else if (startDate && now < startDate) {
      console.log(`🔮 Challenge "${challenge.title}" is FUTURE (now < startDate)`);
      return { status: t('upcoming'), color: 'bg-blue-500', key: 'à-venir' };
    } else {
      console.log(`🟢 Challenge "${challenge.title}" is IN PROGRESS`);
      return { status: t('inProgress'), color: 'bg-green-500', key: 'en-cours' };
    }
  };

  // Filter challenges based on status and thematic filters
  const filteredChallenges = (challenges || []).filter(challenge => {
    const statusInfo = getChallengeStatus(challenge);
    
    // Exclude "No challenge" option (id = 0)
    if (challenge.id === 0) {
      return false;
    }
    
    // Status filter
    const statusMatch = statusFilter === 'all' || statusInfo.key === statusFilter;
    
    // Thematic filter
    const thematicMatch = thematicFilter === 'all' || 
                         (challenge.thematic && challenge.thematic.toLowerCase() === thematicFilter.toLowerCase());
    
    return statusMatch && thematicMatch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredChallenges.length / displayLength);
  const startIndex = (currentPage - 1) * displayLength;
  const endIndex = startIndex + displayLength;
  const currentChallenges = filteredChallenges.slice(startIndex, endIndex);

  // Filter handlers
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleThematicFilterChange = (newThematic) => {
    setThematicFilter(newThematic);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleDisplayLengthChange = (newLength) => {
    setDisplayLength(parseInt(newLength));
    setCurrentPage(1); // Reset to first page when display length changes
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!admin) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex overflow-hidden">
      {/* Sidebar - Now using reusable component */}
      <Sidebar 
        activeSidebarItem="challenges"
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
        dashboardType="streamer"
      />

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 p-[1.20rem] flex justify-between items-center flex-shrink-0">
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div>
              <h2 className="text-xl lg:text-2xl font-bold">{t('challengesList')}</h2>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Search Bar - Hide on very small screens */}
            <div className="relative hidden sm:block">
              <input
                type="text"
                placeholder={t('searchChallengePlaceholder')}
                className="w-48 lg:w-80 bg-gray-700 text-white placeholder-gray-400 px-4 py-2 pl-10 rounded-lg border border-gray-600 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <i className="fas fa-search text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-3 lg:p-6">
          {loadingChallenges ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-400">Chargement des challenges...</div>
            </div>
          ) : (!challenges || challenges.length === 0) ? (
            <div className="text-center py-12">
              <div className="mb-6">
                <i className="fas fa-trophy text-gray-500 text-6xl mb-4"></i>
                <div className="text-xl text-gray-400 mb-2">{t('noChallengesFound')}</div>
                <div className="text-gray-500 mb-6">{t('createFirstChallenge')}</div>
              </div>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/streamer/dashboard')}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-lg font-medium transition-colors mx-2"
                >
                  <i className="fas fa-plus mr-2"></i>
                  {t('createChallenge')}
                </button>
                <div className="text-sm text-gray-600">
                  {t('or')}{' '}
                  <button
                    onClick={() => window.location.reload()}
                    className="text-pink-400 hover:text-pink-300 underline"
                  >
                    {t('refreshPage')}
                  </button>
                  {' '}{t('ifYouThinkError')}
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Filter Section */}
              <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
                {/* Filters Label */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-red-400 font-semibold text-lg mr-4">Filters:</span>
                    
                    {/* Status Filter Dropdown */}
                    <div className="flex flex-wrap items-center space-x-2 mr-4">
                      <select
                        value={statusFilter}
                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent cursor-pointer"
                      >
                        <option value="all">{t('allStatuses')}</option>
                        <option value="à-venir">{t('upcoming')}</option>
                        <option value="en-cours">{t('inProgress')}</option>
                        <option value="terminé">{t('finished')}</option>
                      </select>
                    </div>
                    
                    {/* Thematic Filters */}
                    <div className="flex flex-wrap items-center space-x-2">
                      {[
                        { key: 'sport', label: 'Sport', color: 'border-orange-500 text-orange-400' },
                        { key: 'nourriture', label: 'Nourriture', color: 'border-pink-500 text-pink-400' },
                        { key: 'musique', label: 'Musique', color: 'border-cyan-500 text-cyan-400' },
                        { key: 'art', label: 'Art', color: 'border-yellow-500 text-yellow-400' },
                        { key: 'gaming', label: 'Gaming', color: 'border-cyan-500 text-cyan-400' }
                      ].map((thematic) => (
                        <button
                          key={thematic.key}
                          onClick={() => handleThematicFilterChange(thematic.key)}
                          className={`px-3 py-1 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                            thematicFilter === thematic.key 
                              ? `${thematic.color} bg-opacity-20` 
                              : 'border-gray-600 text-gray-400 hover:border-gray-500'
                          }`}
                        >
                          {thematic.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Create Challenge Button */}
                  <button
                    onClick={() => navigate('/streamer/dashboard#create-challenge')}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="text-lg">+</span>
                    <span>{t('createChallenge')}</span>
                  </button>
                </div>

                {/* Display Length and Summary */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-300">{t('show')}:</label>
                      <select
                        value={displayLength}
                        onChange={(e) => handleDisplayLengthChange(e.target.value)}
                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent cursor-pointer"
                      >
                        <option value="12">12</option>
                        <option value="24">24</option>
                        <option value="50">50</option>
                      </select>
                      <span className="text-sm text-gray-400">par page</span>
                    </div>
                    
                    <div className="text-lg font-medium text-white">
                      {filteredChallenges.length} challenge{filteredChallenges.length > 1 ? 's' : ''} trouvé{filteredChallenges.length > 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  {/* Clear Filters Button */}
                  {(statusFilter !== 'all' || thematicFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setThematicFilter('all');
                        setCurrentPage(1);
                      }}
                      className="text-pink-400 hover:text-pink-300 text-sm underline transition-colors"
                    >
                      Effacer les filtres
                    </button>
                  )}
                </div>
              </div>
              
              {filteredChallenges.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">{t('noChallengesWithFilters')}</div>
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setThematicFilter('all');
                      setCurrentPage(1);
                    }}
                    className="text-pink-400 hover:text-pink-300 underline"
                  >
                    Effacer les filtres
                  </button>
                </div>
              ) : (
                <>
                  {/* Challenges Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                    {currentChallenges.map((challenge) => {
                      const statusInfo = getChallengeStatus(challenge);
                      const rewards = challenge.rewards ? JSON.parse(challenge.rewards) : null;
                      
                      return (
                        <div key={challenge.id} className="bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                          {/* Challenge Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <SecureText 
                                className="text-xl font-bold text-white mb-2 block"
                                title={challenge.title}
                              >
                                {challenge.title}
                              </SecureText>
                              <div className="flex items-center space-x-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${statusInfo.color}`}>
                                  {statusInfo.status}
                                </span>
                                {challenge.thematic && (
                                  <span className="px-3 py-1 rounded-full text-xs font-medium text-white bg-cyan-600">
                                    {challenge.thematic}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Challenge Description */}
                          {challenge.description && (
                            <div className="mb-4">
                              <SecureText className="text-gray-300 text-sm leading-relaxed block">
                                {challenge.description}
                              </SecureText>
                            </div>
                          )}

                          {/* Dates */}
                          <div className="space-y-2 mb-4">
                            {/* Start Date - Always show (use created_at as fallback if start_date is missing) */}
                            <div className="flex items-center text-sm text-gray-300">
                              <i className="fas fa-play-circle text-green-500 w-4 mr-2"></i>
                              <span className="font-medium">Début: {formatDate(challenge.start_date || challenge.created_at)}</span>
                            </div>
                            {/* End Date */}
                            {challenge.expiration && (
                              <div className="flex items-center text-sm text-gray-300">
                                <i className="fas fa-stop-circle text-red-500 w-4 mr-2"></i>
                                <span className="font-medium">Fin: {formatDate(challenge.expiration)}</span>
                              </div>
                            )}
                            {/* Created Date */}
                            <div className="flex items-center text-sm text-gray-400">
                              <i className="fas fa-calendar text-blue-500 w-4 mr-2"></i>
                              <span>{t('created')}: {formatDate(challenge.created_at)}</span>
                            </div>
                          </div>

                          {/* Rewards */}
                          {rewards && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-cyan-400 mb-2">Récompenses:</h4>
                              <div className="space-y-1 text-xs">
                                {rewards.first && rewards.first.length > 0 && (
                                  <div className="flex items-center space-x-2">
                                    <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                      <span className="text-black font-bold text-xs">1</span>
                                    </div>
                                    <span className="text-gray-300">{rewards.first.join(', ')}</span>
                                  </div>
                                )}
                                {rewards.second && rewards.second.length > 0 && (
                                  <div className="flex items-center space-x-2">
                                    <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                                      <span className="text-black font-bold text-xs">2</span>
                                    </div>
                                    <span className="text-gray-300">{rewards.second.join(', ')}</span>
                                  </div>
                                )}
                                {rewards.third && rewards.third.length > 0 && (
                                  <div className="flex items-center space-x-2">
                                    <div className="w-4 h-4 bg-amber-600 rounded-full flex items-center justify-center">
                                      <span className="text-black font-bold text-xs">3</span>
                                    </div>
                                    <span className="text-gray-300">{rewards.third.join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-1 sm:space-x-2">
                      {/* Previous Button */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                          currentPage === 1
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105'
                        }`}
                      >
                        <i className="fa-solid fa-chevron-left mr-1 sm:mr-2"></i>
                        <span className="hidden sm:inline">{t('previous')}</span>
                        <span className="sm:hidden">{t('prev')}</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="flex space-x-1">
                        {[...Array(totalPages)].map((_, index) => {
                          const page = index + 1;
                          const isCurrentPage = page === currentPage;
                          
                          // Show all pages if total pages <= 7, otherwise show smart pagination
                          if (totalPages <= 7) {
                            return (
                              <button
                                key={page}
                                onClick={() => goToPage(page)}
                                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                  isCurrentPage
                                    ? 'bg-pink-600 text-white shadow-lg scale-110'
                                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          } else {
                            // Smart pagination for many pages
                            const showPage = 
                              page === 1 || 
                              page === totalPages || 
                              (page >= currentPage - 1 && page <= currentPage + 1);
                            
                            if (showPage) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => goToPage(page)}
                                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                    isCurrentPage
                                      ? 'bg-pink-600 text-white shadow-lg scale-110'
                                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            } else if (page === currentPage - 2 || page === currentPage + 2) {
                              return (
                                <span key={page} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-500 text-sm sm:text-base">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          }
                        })}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                          currentPage === totalPages
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105'
                        }`}
                      >
                        <span className="hidden sm:inline">{t('next')}</span>
                        <span className="sm:hidden">{t('nextShort')}</span>
                        <i className="fa-solid fa-chevron-right ml-1 sm:ml-2"></i>
                      </button>
                    </div>
                  )}

                  {/* Pagination Info */}
                  {filteredChallenges.length > 0 && (
                    <div className="text-center mt-6">
                      <p className="text-gray-400 text-sm">
                        Affichage de {Math.min(startIndex + 1, filteredChallenges.length)} à {Math.min(endIndex, filteredChallenges.length)} sur {filteredChallenges.length} challenge{filteredChallenges.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-[100] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChallengesList; 